import React, { useState, useEffect, useCallback } from 'react';
import {
  Calendar,
  Loader,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import {
  formatDeadlineDate,
  extractDeadlinesFromText,
  classifyDeadlinesFromExtracted,
  getFederalRegisterMainText,
  parseTimelineDeadlineDate,
  isTimelineRowStale,
  ExtractedDeadline,
} from '../../utils/nprmHelpers';
import {
  findNPRMInFederalRegister,
} from '../../utils/federalRegisterHelpers';
import { notifyCacheHit } from '../../lib/cacheAlertService';

interface TimelineSectionProps {
  docketNumber: string;
  proceedingName?: string;
  /**
   * Extension-aware, PN-preferred comment/reply deadlines resolved by
   * useDocketMetadata (the same values the header card shows). When present
   * they take precedence over this component's own nprm_timelines-derived
   * dates, so the timeline node can't contradict the header — e.g. after a
   * bureau order extended the comment deadline.
   */
  resolvedCommentDeadline?: Date | null;
  resolvedReplyCommentDeadline?: Date | null;
}

// Helper function to check if timeline has all required dates
function isTimelineComplete(timeline: TimelineRecord | null): boolean {
  if (!timeline) return false;
  
  // Check if timeline has all key dates
  return !!(
    timeline.fcc_release_date &&
    timeline.federal_register_publication_date &&
    timeline.comment_deadline &&
    timeline.reply_comment_deadline
  );
}

interface TimelineRecord {
  id: string;
  docket_number: string;
  submission_id: string;
  fcc_release_date: string;
  federal_register_publication_date: string | null;
  comment_deadline: string | null;
  reply_comment_deadline: string | null;
  comments_period_start: string;
  comments_period_end: string | null;
  reply_comments_period_start: string | null;
  reply_comments_period_end: string | null;
  nprm_name: string | null;
  created_at: string;
  updated_at: string;
}

interface TimelineEvent {
  date: Date;
  title: string;
  description: string;
  type: 'fcc_release' | 'federal_register' | 'comment_deadline' | 'reply_comment_deadline' | 'extracted_deadline';
}

// Helper function to format date for timeline tag (e.g., "JAN, 2023" or "JAN 15, 2023")
// Uses UTC to avoid timezone issues
function formatDateTag(date: Date): string {
  const month = date.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' }).toUpperCase();
  const day = date.getUTCDate();
  const year = date.getUTCFullYear();
  return `${month} ${day}, ${year}`;
}

// Build timeline events array in chronological order.
// For stale rows (FR pub date precedes FCC release date — i.e. matched against an
// older NPRM cycle that we couldn't refresh), suppress the FR-derived events so we
// don't display incorrect publication / comment / reply dates. Only the FCC
// release event is shown until the row is repopulated for the current cycle.
function buildTimelineEvents(
  timeline: TimelineRecord,
  extractedDeadlines: ExtractedDeadline[] = [],
  overrides: { commentDeadline?: Date | null; replyCommentDeadline?: Date | null } = {}
): TimelineEvent[] {
  const events: TimelineEvent[] = [];
  const stale = isTimelineRowStale(timeline);

  const fccReleaseDate = new Date(timeline.fcc_release_date);
  const federalRegisterDate = !stale && timeline.federal_register_publication_date
    ? new Date(timeline.federal_register_publication_date)
    : null;
  // Prefer the header's resolved (extension-aware, PN-preferred) deadline when
  // one was passed in; fall back to this row's raw nprm_timelines value only
  // when the caller has nothing better. This is what keeps the timeline node
  // from contradicting the "Comments due" card above it.
  const commentDeadline = (!stale && overrides.commentDeadline)
    ? overrides.commentDeadline
    : (!stale && timeline.comment_deadline ? new Date(timeline.comment_deadline) : null);
  const replyCommentDeadline = (!stale && overrides.replyCommentDeadline)
    ? overrides.replyCommentDeadline
    : (!stale && timeline.reply_comment_deadline ? new Date(timeline.reply_comment_deadline) : null);
  
  // Normalize dates to UTC midnight to avoid timezone display issues
  const normalizeToUTCMidnight = (date: Date): Date => {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0));
  };
  
  const normalizedCommentDeadline = commentDeadline ? normalizeToUTCMidnight(commentDeadline) : null;
  const normalizedReplyCommentDeadline = replyCommentDeadline ? normalizeToUTCMidnight(replyCommentDeadline) : null;

  // Track dates we've already added to avoid duplicates
  const addedDates = new Set<string>();

  // FCC Release Date (always present)
  events.push({
    date: fccReleaseDate,
    title: 'NPRM Released by FCC',
    description: 'The Notice of Proposed Rulemaking was released by the Federal Communications Commission.',
    type: 'fcc_release',
  });
  addedDates.add(fccReleaseDate.toISOString().split('T')[0]);

  // Federal Register Publication Date
  if (federalRegisterDate) {
    events.push({
      date: federalRegisterDate,
      title: 'Published in Federal Register',
      description: 'The NPRM was published in the Federal Register, starting the official comment period.',
      type: 'federal_register',
    });
    addedDates.add(federalRegisterDate.toISOString().split('T')[0]);
  }

  // Comment Deadline (use normalized UTC date)
  if (normalizedCommentDeadline) {
    const commentFormatted = formatDeadlineDate(normalizedCommentDeadline, 'comment');
    const relativeText = commentFormatted.relative ? ` (${commentFormatted.relative})` : '';
    events.push({
      date: normalizedCommentDeadline,
      title: 'Comments Due',
      description: `Deadline for submitting initial comments${federalRegisterDate ? ' (from Federal Register)' : ''}.${relativeText}`,
      type: 'comment_deadline',
    });
    addedDates.add(normalizedCommentDeadline.toISOString().split('T')[0]);
  }

  // Reply Comment Deadline (use normalized UTC date)
  if (normalizedReplyCommentDeadline) {
    const replyFormatted = formatDeadlineDate(normalizedReplyCommentDeadline, 'reply_comment');
    const relativeText = replyFormatted.relative ? ` (${replyFormatted.relative})` : '';
    events.push({
      date: normalizedReplyCommentDeadline,
      title: 'Reply Comments Due',
      description: `Deadline for submitting reply comments${federalRegisterDate ? ' (from Federal Register)' : ''}.${relativeText}`,
      type: 'reply_comment_deadline',
    });
    addedDates.add(normalizedReplyCommentDeadline.toISOString().split('T')[0]);
  }

  // Note: extractedDeadlines are still used to identify commentDeadline and replyCommentDeadline
  // (see lines 256-360), but we don't display other extracted deadlines as separate events

  // Sort by date (oldest first)
  return events.sort((a, b) => a.date.getTime() - b.date.getTime());
}

export const TimelineSection: React.FC<TimelineSectionProps> = ({
  docketNumber,
  proceedingName,
  resolvedCommentDeadline,
  resolvedReplyCommentDeadline,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timelines, setTimelines] = useState<TimelineRecord[]>([]);
  const [extractedDeadlinesMap, setExtractedDeadlinesMap] = useState<Map<string, ExtractedDeadline[]>>(new Map());
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  // Helper function to auto-populate timeline if missing or incomplete
  const autoPopulateTimeline = useCallback(async (): Promise<boolean> => {
    if (!docketNumber) return false;

    try {
      // Fetch NPRM filings to get submission ID and FCC release date
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-nprm-filings`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          docketNumber: docketNumber,
        }),
      });

      if (!response.ok) {
        console.warn('Failed to fetch NPRM filings for auto-population');
        return false;
      }

      const data = await response.json();
      notifyCacheHit(data, `Timeline NPRM filings for ${docketNumber}`);
      const filings = data?.filing || [];

      if (filings.length === 0) {
        console.log('No NPRM filings found for auto-population');
        return false;
      }

      // Get the most recent NPRM filing
      const mostRecentFiling = filings.sort((a: any, b: any) => {
        const dateA = new Date(a.date_received || a.date_submission || 0);
        const dateB = new Date(b.date_received || b.date_submission || 0);
        return dateB.getTime() - dateA.getTime();
      })[0];

      const submissionId = mostRecentFiling.id_submission;
      const fccReleaseDate = mostRecentFiling.date_received || mostRecentFiling.date_submission;

      if (!fccReleaseDate) {
        console.warn('No FCC release date found in NPRM filing');
        return false;
      }

      // Use proceeding name if available, otherwise try to get from filing
      const nprmName = proceedingName || mostRecentFiling.proceedings?.[0]?.name || null;

      // Find NPRM in Federal Register, gated to the current cycle (don't accept FR
      // docs that predate the most recent NPRM filing's FCC release date).
      const fccReleaseAsDate =
        fccReleaseDate && !Number.isNaN(Date.parse(fccReleaseDate))
          ? new Date(fccReleaseDate)
          : undefined;
      const nprmTitles = nprmName ? [nprmName] : [docketNumber];
      const matchResult = await findNPRMInFederalRegister(
        docketNumber,
        nprmTitles,
        fccReleaseAsDate
      );

      if (!matchResult.document || !matchResult.publicationDate) {
        // Create timeline with just FCC release date
        const timelineApiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/populate-nprm-timeline`;
        await fetch(timelineApiUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            docketNumber: docketNumber,
            submissionId: submissionId,
            fccReleaseDate: fccReleaseDate,
            nprmName: nprmName,
          }),
        });
        return true;
      }

      // Prefer FR's structured deadline fields when present — they're precise and
      // avoid an HTML round-trip. `comments_close_on` is YYYY-MM-DD; `dates` is a
      // human-readable summary that frequently carries the reply deadline too.
      let foundCommentDeadline: Date | null = null;
      let foundReplyCommentDeadline: Date | null = null;

      const matchedDoc = matchResult.document;
      if (matchedDoc?.comments_close_on) {
        foundCommentDeadline = parseTimelineDeadlineDate(matchedDoc.comments_close_on);
      }
      if (matchedDoc?.dates) {
        const datesExtracted = extractDeadlinesFromText(matchedDoc.dates);
        const datesClassified = classifyDeadlinesFromExtracted(datesExtracted, matchedDoc.dates);
        if (!foundCommentDeadline && datesClassified.foundCommentDeadline) {
          foundCommentDeadline = datesClassified.foundCommentDeadline;
        }
        if (!foundReplyCommentDeadline && datesClassified.foundReplyCommentDeadline) {
          foundReplyCommentDeadline = datesClassified.foundReplyCommentDeadline;
        }
      }

      // HTML fallback only when structured fields didn't fully populate both.
      // Writes go to local temporaries first so an HTML hit can't overwrite a
      // structured value we already have.
      if (!foundCommentDeadline || !foundReplyCommentDeadline) {
        const htmlApiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-federal-register-html`;
        const htmlResponse = await fetch(htmlApiUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ htmlUrl: matchResult.document.html_url }),
        });

        if (!htmlResponse.ok) {
          // Still create / update timeline with Federal Register publication date.
          const timelineApiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/populate-nprm-timeline`;
          await fetch(timelineApiUrl, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              docketNumber: docketNumber,
              submissionId: submissionId,
              fccReleaseDate: fccReleaseDate,
              federalRegisterPublicationDate: matchResult.publicationDate.toISOString(),
              commentDeadline: foundCommentDeadline ? foundCommentDeadline.toISOString() : null,
              replyCommentDeadline: foundReplyCommentDeadline ? foundReplyCommentDeadline.toISOString() : null,
              nprmName: matchResult.document?.title || nprmName,
            }),
          });
          return true;
        }

        const htmlResult = await htmlResponse.json();
        const html = htmlResult.html;

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const mainContent = doc.querySelector('.document-content, .document-full-text, main, article, [role="main"]')
          || doc.querySelector('#document-content, #document-full-text')
          || doc.body;
        const textContent = mainContent.textContent || doc.body.textContent || '';

        const extracted = extractDeadlinesFromText(textContent);

        let htmlComment: Date | null = null;
        let htmlReply: Date | null = null;

        if (extracted.length > 0) {
          for (const deadline of extracted) {
            const context = deadline.context.toLowerCase();

            if (deadline.isReplyComment === true) {
              if (!htmlReply || deadline.date > htmlReply) {
                htmlReply = deadline.date;
              }
              continue;
            }

            if (context.includes('reply comments will be accepted until') ||
                context.includes('reply comments are accepted until') ||
                context.includes('reply comments will be accepted')) {
              if (!htmlReply || deadline.date > htmlReply) {
                htmlReply = deadline.date;
              }
              continue;
            }

            if ((context.includes('comments will be accepted until') ||
                 context.includes('comments are accepted until')) &&
                !context.includes('reply')) {
              if (!htmlComment || deadline.date < htmlComment) {
                htmlComment = deadline.date;
              }
              continue;
            }
          }

          if (!htmlComment || !htmlReply) {
            for (const deadline of extracted) {
              const context = deadline.context.toLowerCase();

              if (!htmlReply &&
                  (context.includes('reply comments') || context.includes('reply comment'))) {
                if (!htmlReply || deadline.date > htmlReply) {
                  htmlReply = deadline.date;
                }
              } else if (!htmlComment &&
                         (context.includes('comments are due') || context.includes('comments due') ||
                          context.includes('comment deadline') || context.includes('comment period')) &&
                         !context.includes('reply')) {
                if (!htmlComment || deadline.date < htmlComment) {
                  htmlComment = deadline.date;
                }
              }
            }
          }

          if (!htmlComment && !htmlReply && extracted.length > 0) {
            const sortedDeadlines = extracted.sort((a, b) => a.date.getTime() - b.date.getTime());
            if (sortedDeadlines.length >= 1) {
              htmlComment = sortedDeadlines[0].date;
            }
            if (sortedDeadlines.length >= 2) {
              htmlReply = sortedDeadlines[1].date;
            }
          }
        }

        // Merge HTML hits into the structured-field results (only fill missing).
        if (!foundCommentDeadline && htmlComment) foundCommentDeadline = htmlComment;
        if (!foundReplyCommentDeadline && htmlReply) foundReplyCommentDeadline = htmlReply;
      }

      // Populate timeline in Supabase with all extracted dates
      const timelineApiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/populate-nprm-timeline`;
      await fetch(timelineApiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          docketNumber: docketNumber,
          submissionId: submissionId,
          fccReleaseDate: fccReleaseDate,
          federalRegisterPublicationDate: matchResult.publicationDate.toISOString(),
          commentDeadline: foundCommentDeadline ? foundCommentDeadline.toISOString() : null,
          replyCommentDeadline: foundReplyCommentDeadline ? foundReplyCommentDeadline.toISOString() : null,
          nprmName: matchResult.document?.title || nprmName,
        }),
      });

      console.log('Timeline auto-populated successfully');
      return true;
    } catch (err) {
      console.error('Error auto-populating timeline:', err);
      return false;
    }
  }, [docketNumber, proceedingName]);

  const fetchTimelines = useCallback(async (isRefresh = false) => {
    if (!docketNumber) {
      setLoading(false);
      return;
    }

    try {
      if (!isRefresh) {
        setLoading(true);
      }
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('nprm_timelines')
        .select('*')
        .eq('docket_number', docketNumber)
        .order('fcc_release_date', { ascending: false });

      if (fetchError) {
        throw fetchError;
      }

      // Check if timeline exists, is complete, and is for the current NPRM cycle.
      // A row whose FR pub date precedes its FCC release date was matched against an
      // older NPRM cycle and must be repopulated so the displayed timeline reflects
      // the current NPRM (e.g. 02-278's 2026-03-27 NPRM -> FR 2026-04-23 publication).
      let timelinesToProcess = data || [];
      const needsAutoPopulation =
        timelinesToProcess.length === 0 ||
        timelinesToProcess.some(
          (timeline) => !isTimelineComplete(timeline) || isTimelineRowStale(timeline)
        );

      // Auto-populate when missing, incomplete, or stale. Reload (isRefresh) is also
      // allowed to trigger repopulation so users can recover from stale rows manually.
      if (needsAutoPopulation) {
        console.log('Timeline missing, incomplete, or stale - auto-populating...');
        const populated = await autoPopulateTimeline();
        
        if (populated) {
          // Refetch timelines after auto-population
          const { data: updatedData, error: refetchError } = await supabase
            .from('nprm_timelines')
            .select('*')
            .eq('docket_number', docketNumber)
            .order('fcc_release_date', { ascending: false });
          
          if (!refetchError && updatedData) {
            timelinesToProcess = updatedData;
            setTimelines(updatedData);
          } else {
            setTimelines(timelinesToProcess);
          }
        } else {
          setTimelines(timelinesToProcess);
        }
      } else {
        setTimelines(timelinesToProcess);
      }

      // Fetch Federal Register documents and extract deadlines for each timeline
      // Only extract if timeline is incomplete (missing comment or reply comment deadlines)
      if (timelinesToProcess && timelinesToProcess.length > 0) {
        const deadlinesMap = new Map<string, ExtractedDeadline[]>();
        const updatePromises: Promise<boolean>[] = [];
        
        await Promise.all(
          timelinesToProcess.map(async (timeline) => {
            // Re-extract when the row is missing deadlines OR is stale (FR pub date
            // precedes the FCC release date — i.e. matched against an older NPRM).
            // The row's fcc_release_date is also threaded into findNPRMInFederalRegister
            // so the refresh can't pull deadlines from a previous cycle.
            const stale = isTimelineRowStale(timeline);
            const isIncomplete =
              !timeline.comment_deadline || !timeline.reply_comment_deadline || stale;

            if (timeline.nprm_name && isIncomplete) {
              try {
                const nprmTitles = timeline.nprm_name ? [timeline.nprm_name] : undefined;
                const minPub =
                  timeline.fcc_release_date && !Number.isNaN(Date.parse(timeline.fcc_release_date))
                    ? new Date(timeline.fcc_release_date)
                    : undefined;
                const matchResult = await findNPRMInFederalRegister(
                  docketNumber,
                  nprmTitles,
                  minPub
                );

                if (matchResult.document) {
                  // Prefer FR's structured deadline fields first, then HTML fallback.
                  let foundCommentDeadline: Date | null = null;
                  let foundReplyCommentDeadline: Date | null = null;

                  const matchedDoc = matchResult.document;
                  if (matchedDoc.comments_close_on) {
                    foundCommentDeadline = parseTimelineDeadlineDate(matchedDoc.comments_close_on);
                  }
                  if (matchedDoc.dates) {
                    const datesExtracted = extractDeadlinesFromText(matchedDoc.dates);
                    const datesClassified = classifyDeadlinesFromExtracted(datesExtracted, matchedDoc.dates);
                    if (!foundCommentDeadline && datesClassified.foundCommentDeadline) {
                      foundCommentDeadline = datesClassified.foundCommentDeadline;
                    }
                    if (!foundReplyCommentDeadline && datesClassified.foundReplyCommentDeadline) {
                      foundReplyCommentDeadline = datesClassified.foundReplyCommentDeadline;
                    }
                  }

                  if ((!foundCommentDeadline || !foundReplyCommentDeadline) && matchedDoc.html_url) {
                    try {
                      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-federal-register-html`;
                      const response = await fetch(apiUrl, {
                        method: 'POST',
                        headers: {
                          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                          'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ htmlUrl: matchedDoc.html_url }),
                      });

                      if (response.ok) {
                        const result = await response.json();
                        const html = result.html;
                        const textContent = getFederalRegisterMainText(html);
                        const extracted = extractDeadlinesFromText(textContent);

                        if (extracted.length > 0) {
                          deadlinesMap.set(timeline.id, extracted);
                          const classified = classifyDeadlinesFromExtracted(extracted, textContent);
                          if (!foundCommentDeadline && classified.foundCommentDeadline) {
                            foundCommentDeadline = classified.foundCommentDeadline;
                          }
                          if (!foundReplyCommentDeadline && classified.foundReplyCommentDeadline) {
                            foundReplyCommentDeadline = classified.foundReplyCommentDeadline;
                          }
                        }
                      }
                    } catch (fetchErr) {
                      // Failed to fetch Federal Register document
                    }
                  }

                  // Update only when we have at least one deadline OR when the row is
                  // stale (so we can overwrite the wrong FR pub date / nprm_name).
                  if (foundCommentDeadline || foundReplyCommentDeadline || stale) {
                    const updatePromise = (async () => {
                      try {
                        const timelineApiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/populate-nprm-timeline`;
                        // For stale rows, force-overwrite the FR-derived fields with
                        // the new (cycle-correct) match's values; otherwise prefer
                        // the existing row's values to avoid clobbering good data.
                        const newFrPub = matchResult.publicationDate?.toISOString();
                        const updateResponse = await fetch(timelineApiUrl, {
                          method: 'POST',
                          headers: {
                            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                            'Content-Type': 'application/json',
                          },
                          body: JSON.stringify({
                            docketNumber: docketNumber,
                            submissionId: timeline.submission_id,
                            fccReleaseDate: timeline.fcc_release_date,
                            federalRegisterPublicationDate: stale
                              ? (newFrPub || timeline.federal_register_publication_date)
                              : (timeline.federal_register_publication_date || newFrPub),
                            commentDeadline: foundCommentDeadline
                              ? foundCommentDeadline.toISOString()
                              : (stale ? null : timeline.comment_deadline),
                            replyCommentDeadline: foundReplyCommentDeadline
                              ? foundReplyCommentDeadline.toISOString()
                              : (stale ? null : timeline.reply_comment_deadline),
                            nprmName: stale
                              ? (matchedDoc.title || timeline.nprm_name)
                              : timeline.nprm_name,
                          }),
                        });

                        if (updateResponse.ok) {
                          await updateResponse.json();
                          return true;
                        } else {
                          const errorText = await updateResponse.text();
                          console.error('❌ Failed to update timeline:', updateResponse.status, errorText);
                          return false;
                        }
                      } catch (updateErr) {
                        console.error('❌ Error updating timeline:', updateErr);
                        return false;
                      }
                    })();
                    updatePromises.push(updatePromise);
                  }
                }
              } catch (err) {
                // Failed to find Federal Register document
              }
            }
          })
        );

        setExtractedDeadlinesMap(deadlinesMap);
        
        // Wait for all timeline updates to complete, then refetch if any succeeded
        // Always refetch after updates, not just on refresh, so timeline displays updated dates
        if (updatePromises.length > 0) {
          const updateResults = await Promise.all(updatePromises);
          const anyUpdated = updateResults.some(result => result === true);
          
          if (anyUpdated) {
            const { data: updatedData, error: refetchError } = await supabase
              .from('nprm_timelines')
              .select('*')
              .eq('docket_number', docketNumber)
              .order('fcc_release_date', { ascending: false });
            
            if (!refetchError && updatedData) {
              setTimelines(updatedData);
            }
          }
        }
      }
      
      // Update last refreshed timestamp
      setLastRefreshed(new Date());
    } catch (err) {
      console.error('TimelineSection: Error fetching timelines:', err);
      setError(err instanceof Error ? err.message : 'Failed to load timeline data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [docketNumber, autoPopulateTimeline]);

  useEffect(() => {
    fetchTimelines();
  }, [fetchTimelines]);

  const handleReload = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('Reload button clicked, refreshing timeline...');
    setRefreshing(true);
    try {
      await fetchTimelines(true); // Pass true to indicate it's a refresh
      console.log('Timeline reload completed');
    } catch (err) {
      console.error('Error during reload:', err);
      setRefreshing(false);
      setError(err instanceof Error ? err.message : 'Failed to reload timeline');
    }
  };

  if (loading) {
    return (
      <div className="mb-8 backdrop-blur-xl bg-white/90 border border-green-300/50 rounded-lg p-6 shadow-sm">
        <div className="flex items-center justify-center py-8">
          <Loader className="w-6 h-6 text-green-600 animate-spin" />
          <span className="ml-3 text-gray-600">Loading timeline...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-8 backdrop-blur-xl bg-white/90 border border-red-300/50 rounded-lg p-6 shadow-sm">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-red-800 font-semibold mb-2">Error Loading Timeline</h3>
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (timelines.length === 0) {
    return null; // Don't show section if no timelines
  }

  return (
    <div className="mb-8 backdrop-blur-xl bg-white/90 border border-green-300/50 rounded-lg p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <Calendar className="w-6 h-6 text-green-600" />
          <h2 className="text-2xl font-bold text-black">NPRM Timeline</h2>
          {timelines.length > 1 && (
            <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full font-medium">
              {timelines.length} NPRMs
            </span>
          )}
          {lastRefreshed && !refreshing && (
            <span className="text-xs text-gray-500">
              Updated {lastRefreshed.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
            </span>
          )}
        </div>
        <button
          onClick={handleReload}
          disabled={refreshing || loading}
          className="flex items-center space-x-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed active:bg-gray-300"
          title={refreshing ? "Reloading timeline..." : "Reload timeline"}
          type="button"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          <span>{refreshing ? 'Reloading...' : 'Reload'}</span>
        </button>
      </div>

      <div className="space-y-8">
        {timelines.map((timeline) => {
          const extractedDeadlines = extractedDeadlinesMap.get(timeline.id) || [];
          const events = buildTimelineEvents(timeline, extractedDeadlines, {
            commentDeadline: resolvedCommentDeadline,
            replyCommentDeadline: resolvedReplyCommentDeadline,
          });
          
          if (events.length === 0) {
            return null;
          }

          const stale = isTimelineRowStale(timeline);
          return (
            <div key={timeline.id} className="relative">
              {/* NPRM Title if available; suppress when the row is stale so we don't
                  display a previous cycle's title alongside a current FCC release. */}
              {timeline.nprm_name && !stale && (
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {timeline.nprm_name}
                </h3>
              )}

              {/* Vertical Timeline */}
              <div className="relative">
                {/* Continuous Vertical Line */}
                <div className="absolute left-[124px] top-0 bottom-0 w-0.5 bg-gray-300"></div>

                {/* Timeline Events */}
                <div className="space-y-6">
                  {events.map((event, index) => {
                    const dateTag = formatDateTag(event.date);

                    return (
                      <div key={index} className="relative flex items-start">
                        {/* Date Tag on Left */}
                        <div className="flex-shrink-0 w-28 mr-6">
                          <div className="bg-green-100 text-green-800 px-3 py-1.5 rounded-lg text-sm font-medium text-center">
                            {dateTag}
                          </div>
                        </div>

                        {/* Blue Dot - positioned on the vertical line */}
                        <div className="absolute left-[124px] top-2 -translate-x-1/2 z-10">
                          <div className="w-3 h-3 bg-blue-500 rounded-full border-2 border-white shadow-sm"></div>
                        </div>

                        {/* Event Content on Right */}
                        <div className="flex-1 pt-0.5 pl-6">
                          <h4 className="font-bold text-gray-900 mb-1">{event.title}</h4>
                          <p className="text-sm text-gray-600 leading-relaxed">{event.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

